import { supabase } from '@/lib/supabase/client'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { Canvg } from 'canvg'
import { drawMethodTitle, getNdtResultBodyStyles, getNdtResultTableOptions } from './pdfTitle'
import type { ETReportData } from '@/types/etTypes'
import { resolveSignatureUrl } from '@/utils/signatureUrl'
import { findProfileByName } from './ndtPdfShared'

// ─── Border + typography (matches PrototypeControlCardTemplate) ─────────────
// Kept in sync with features/control-card/presentation/templates/
// PrototypeControlCardTemplate so ET reports and control cards share the
// same hairline gray chrome and type system. Don't tune these in isolation
// — update both templates together if the look needs to change.
const BORDER_COLOR: [number, number, number] = [100, 100, 100]
const BORDER_W = 0.1
const TEXT_COLOR: [number, number, number] = [0, 0, 0]
const LABEL_COLOR: [number, number, number] = [68, 68, 68] // #444
const LABEL_SIZE = 6.5
const VALUE_SIZE = 8

// Helper function to format date
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB')
}

// SVG for STM logo
const STM_LOGO_SVG = `<svg width="12662" height="3631" viewBox="0 0 12662 3631" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M3824.18 2586.89C3698.69 2586.89 3590.68 2568 3500.16 2530.22C3409.64 2492.44 3326.32 2443.54 3250.2 2383.54L3105.16 2543.55H3012.59V1843.49H3108.25C3132.94 1952.39 3173.05 2054.62 3228.6 2150.18C3286.2 2245.75 3363.35 2323.53 3460.04 2383.54C3556.73 2441.32 3675.03 2470.21 3814.92 2470.21C3930.13 2470.21 4025.79 2450.21 4101.91 2410.21C4180.09 2370.2 4238.72 2315.75 4277.81 2246.86C4318.95 2175.74 4339.53 2097.96 4339.53 2013.51C4339.53 1926.83 4321.01 1856.83 4283.98 1803.49C4246.95 1750.15 4198.6 1709.04 4138.94 1680.14C4081.34 1649.03 4018.59 1624.58 3950.7 1606.8C3884.87 1589.02 3822.12 1572.36 3762.46 1556.8C3618.45 1516.8 3491.93 1464.57 3382.89 1400.12C3273.86 1333.45 3188.48 1247.88 3126.76 1143.43C3067.1 1038.98 3037.27 908.967 3037.27 753.398C3037.27 582.272 3071.22 441.149 3139.11 330.028C3207 218.908 3296.49 136.678 3407.58 83.3404C3518.67 27.7801 3636.97 0 3762.46 0C3855.04 0 3937.33 14.4456 4009.33 43.3369C4083.4 72.2283 4161.57 114.454 4243.86 170.015L4364.21 30.0026H4459.88V710.061H4367.3C4307.64 498.932 4227.4 347.807 4126.6 256.689C4025.79 163.347 3902.36 116.677 3756.29 116.677C3667.83 116.677 3586.56 132.233 3512.5 163.347C3440.5 192.239 3382.89 234.464 3339.69 290.025C3296.49 345.585 3274.89 411.146 3274.89 486.708C3274.89 557.825 3293.4 616.719 3330.43 663.39C3367.46 707.838 3415.81 743.397 3475.47 770.066C3535.13 796.735 3598.91 818.959 3666.8 836.738C3734.69 854.518 3799.49 871.186 3861.21 886.743C4017.56 926.746 4149.23 982.306 4256.21 1053.42C4363.18 1122.32 4444.45 1213.44 4499.99 1326.78C4555.54 1440.12 4583.31 1582.36 4583.31 1753.48C4583.31 2015.73 4517.48 2220.19 4385.81 2366.87C4256.21 2513.55 4068.99 2586.89 3824.18 2586.89Z" fill="#3A3A3A"/>
  <path d="M5149.07 2543.55V2443.54H5534.81V130.011H5380.51C5267.36 130.011 5169.64 163.347 5087.35 230.02C5007.12 296.692 4944.37 384.477 4899.11 493.376C4855.91 602.274 4833.28 720.062 4831.22 846.739H4707.79V30.0026H6849.41V846.739H6725.97C6723.91 720.062 6700.25 602.274 6654.99 493.376C6611.79 384.477 6550.07 296.692 6469.84 230.02C6389.6 163.347 6291.88 130.011 6176.68 130.011H6022.38V2443.54H6386.52V2543.55H5149.07Z" fill="#3A3A3A"/>
  <path d="M8104.24 2573.55L7443.85 263.356H7431.51V2443.54H7770.96V2543.55H6956.28V2443.54H7295.73V130.011H6956.28V30.0026H7931.43L8366.54 1546.8H8378.88L8814 30.0026H9708.91V130.011H9387.97V2443.54H9708.91V2543.55H8570.21V2443.54H8900.4V263.356H8888.06L8230.76 2573.55H8104.24Z" fill="#3A3A3A"/>
  <path d="M6331 3631L0 2941.29H12662L6331 3631Z" fill="#FB4B4E"/>
</svg>`;

// Use canvg to render SVG to canvas and get data URL
async function getLogoDataUrl(): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }
  
  // Set canvas size to desired dimensions - higher resolution for better quality
  canvas.width = 400; // Doubled for better quality
  canvas.height = 120; // Doubled for better quality
  
  try {
    // Create a new Canvg instance
    const v = await Canvg.fromString(ctx, STM_LOGO_SVG);
    
    // Scale SVG to fit canvas
    v.resize(400, 120, 'xMidYMid meet');
    
    // Render SVG
    await v.render();
    
    // Get data URL - full quality PNG
    return canvas.toDataURL('image/png');
  } catch (error) {
    return '';
  }
}

// Helper function to convert image to base64
function getBase64Image(imgUrl: string): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.setAttribute("crossOrigin", "anonymous")

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx?.drawImage(img, 0, 0)
      try {
        const dataURL = canvas.toDataURL("image/png")
        resolve(dataURL)
      } catch (err) {
        resolve(null)
      }
    }

    img.onerror = error => {
      resolve(null)
    }

    img.src = imgUrl
  })
}

// Function to get inspector data including certificate and equipment
async function getInspectorData(fullName: string): Promise<{
  certificateNo: string | null;
  etLevel: number | null;
  etEquipment: string | null;
}> {
  try {
    const { data: inspector, error } = await supabase
      .from('profiles')
      .select('certificate_no, et_level, et_equipment')
      .eq('full_name', fullName)
      .single()

    if (error || !inspector) {
      return {
        certificateNo: null,
        etLevel: null,
        etEquipment: null
      }
    }

    return {
      certificateNo: inspector.certificate_no || null,
      etLevel: inspector.et_level || null,
      etEquipment: inspector.et_equipment || null
    };
  } catch (error) {
    return {
      certificateNo: null,
      etLevel: null,
      etEquipment: null
    }
  }
}

// Function to get signature for a user (matches MT/LT lookup behaviour)
async function getSignature(fullName: string): Promise<string | null> {
  try {
    const profile = await findProfileByName<{
      signature: string | null
      inspectors: { signature?: string | null } | { signature?: string | null }[] | null
    }>(fullName, 'signature, inspectors(signature)')

    const inspectors = profile?.inspectors
    const inspectorSignature = Array.isArray(inspectors) ? inspectors[0]?.signature : inspectors?.signature
    const signatureUrl = profile?.signature || inspectorSignature
    if (!signatureUrl) return null
    return getOptimizedImage(await resolveSignatureUrl(signatureUrl))
  } catch {
    return null
  }
}

// New function to optimize images - but keep full quality
async function getOptimizedImage(imgUrl: string): Promise<string | null> {
  // Check if the URL is valid
  if (!imgUrl || typeof imgUrl !== 'string' || !imgUrl.trim()) {
    return null
  }
  
  // If it's already a data URL, just return it
  if (imgUrl.startsWith('data:')) {
    return imgUrl
  }
  
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");

    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Keep original dimensions but limit maximum size
      const maxDimension = 200; // Increased from 150 for better quality
      let width = img.width;
      let height = img.height;
      
      if (width > height && width > maxDimension) {
        height = (height * maxDimension) / width;
        width = maxDimension;
      } else if (height > maxDimension) {
        width = (width * maxDimension) / height;
        height = maxDimension;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null)
        return
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      try {
        // Use PNG for best quality
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (err) {
        resolve(null);
      }
    };

    img.onerror = error => {
      // Try with a proxy as a fallback for CORS issues
      if (!imgUrl.includes('cors-anywhere')) {
        const proxyUrl = `https://cors-anywhere.herokuapp.com/${imgUrl}`
        img.src = proxyUrl
      } else {
        resolve(null)
      }
    };

    img.src = imgUrl;
  });
}

// Update getFullName function to just return the name since it's already full
function getFullName(name: string): string {
  return name
}

function getExtentOfExamination(reportData: ETReportData): string {
  const parts: string[] = []
  
  const objectTypeStr = String(reportData.objectType);
  
  // Include padeyes for POU, POU w/ISO, and custom objects (custom behaves like POU)
  if (objectTypeStr !== 'Sling') {
    parts.push('100% of welds on padeyes')
    
    // Add "ISO locks weldment" if the object type is "POU w/ISO" (not for custom)
    if (
      objectTypeStr === 'POU w/ISO' || 
      objectTypeStr === 'POU w&#x2F;ISO' ||
      (objectTypeStr.includes('POU') && objectTypeStr.includes('ISO'))
    ) {
      parts.push('ISO locks weldment')
    }
  }
  
  // Add sling-related examinations for sling objects or when a sling ID is present
  if (objectTypeStr === 'Sling' || reportData.slingId) {
    parts.push('100% of sling links')
    if (reportData.isChainSling) {
      parts.push('10% of chain legs')
    }
  }
  
  return parts.join(', ')
}

// Base64 encoded STM logo - using a PNG instead of SVG to avoid compatibility issues
const STM_LOGO_PNG = 'data:image/png;base64,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';

export async function generatePDF(reportData: ETReportData): Promise<Blob> {
  try {
    // Prefer Documents approver; fall back to form Ordreansvarlig (same dual-box
    // behaviour as MT/LT once a second signer is present).
    const verifierName = (reportData.approvedBy || reportData.verifiedBy || '').trim()
    const verifierDate = reportData.approvedAt || reportData.testDate

    // Load all assets in parallel. Verifier signature stays null when no
    // second signer is present so we can render a full-width ISSUED BY box.
    const [inspectorSignature, verifierSignature, logoDataUrl, inspectorData] = await Promise.all([
      getSignature(reportData.issuedBy),
      verifierName ? getSignature(verifierName) : Promise.resolve(null),
      getLogoDataUrl(), // Get the logo from public folder
      getInspectorData(reportData.issuedBy) // Get inspector data from profiles table
    ]);

    // Create new jsPDF document - exact sizing to match original
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Basic settings - exact matching to original
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;
    
    // HEADER - Title and Logo
    drawMethodTitle(doc, 'Eddy Current', margin, margin);
    
    // Add logo with fallback to text if image couldn't be loaded
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12);
    } else {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('STM INSPECTION AS', pageWidth - margin - 20, margin, { align: 'center' });
    }
    
    // Add more space between header and first table
    let yPos = margin + 15; // Increased from 7 to 15 for more space
    
    // COMPACT TABLE OPTIONS - much smaller to match original
    const tableOptions = {
      theme: 'plain' as const,
      styles: {
        fontSize: VALUE_SIZE,
        textColor: TEXT_COLOR,
        cellPadding: 0.5,
        lineColor: BORDER_COLOR,
        lineWidth: { top: 0, right: BORDER_W, bottom: 0, left: BORDER_W }, // Remove horizontal lines, keep vertical
        minCellHeight: 1.5
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        textColor: LABEL_COLOR,
        fontStyle: 'normal' as const,
        fontSize: LABEL_SIZE,
        cellPadding: 0.3,
        lineWidth: { top: BORDER_W, right: BORDER_W, bottom: 0, left: BORDER_W },
        lineColor: BORDER_COLOR,
        halign: 'left' as const,
        valign: 'middle' as const
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        lineWidth: { top: 0, right: BORDER_W, bottom: 0, left: BORDER_W } // Remove horizontal lines, keep vertical
      },
      margin: { left: margin, right: margin },
      tableLineColor: BORDER_COLOR,
      tableLineWidth: BORDER_W
    };

    // Separate options for probe settings table - keep horizontal lines
    const probeTableOptions = {
      theme: 'plain' as const,
      styles: {
        fontSize: VALUE_SIZE,
        textColor: TEXT_COLOR,
        cellPadding: 1.5,
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
        minCellHeight: 3
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        textColor: LABEL_COLOR,
        fontStyle: 'normal' as const,
        fontSize: LABEL_SIZE,
        cellPadding: 1,
        lineWidth: { top: BORDER_W, right: BORDER_W, bottom: 0, left: BORDER_W },
        lineColor: BORDER_COLOR,
        halign: 'center' as const,
        valign: 'middle' as const
      },
      margin: { left: margin, right: margin },
      tableLineColor: BORDER_COLOR,
      tableLineWidth: BORDER_W
    };
    
    // Define standard column widths at the beginning for consistency
    const leftColumnWidth = pageWidth * 0.7 - margin;
    const rightColumnWidth = pageWidth * 0.3 - margin;

    // TEST DATE, LOCATION, REPORT NO
    autoTable(doc, {
      startY: yPos,
      head: [['TEST DATE', 'LOCATION', 'REPORT NO.']],
      body: [[
        formatDate(reportData.testDate), 
        reportData.testSite || 'Bergen', 
        `ET-${reportData.reportNumber ?? 'N/A'}.${reportData.sequenceNumber || '001'}`
      ]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 2 },
        1: { cellWidth: leftColumnWidth / 2 },
        2: { 
          cellWidth: rightColumnWidth, 
          halign: 'left' as const,
          valign: 'middle' as const
        }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // OBJECT and MATERIAL table
    autoTable(doc, {
      startY: yPos,
      head: [['OBJECT', 'MATERIAL']],
      body: [[
        reportData.objectType === 'Sling' ? (reportData.isChainSling ? 'Chain Sling' : 'Wire Sling') : 
        reportData.objectType === 'POU w/ISO' ? 'Portable Offshore Unit with ISO Locks' : 
        reportData.objectType === 'custom' ? (reportData.customObjectType || 'Custom Object') :
        'Portable Offshore Unit', 
        reportData.material
      ]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth },
        1: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // DRAWING, DIAMETER, THICKNESS table - fix blank space
    autoTable(doc, {
      startY: yPos,
      head: [['DRAWING', 'DIAMETER', 'THICKNESS']],
      body: [['---', '---', '---']],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: (pageWidth - 2*margin) - rightColumnWidth }, // Full width minus right columns
        1: { cellWidth: rightColumnWidth / 2 },
        2: { cellWidth: rightColumnWidth / 2 }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // WELDER, WELDING PROCEDURE, WELDING METHOD table
    autoTable(doc, {
      startY: yPos,
      head: [['WELDER', 'WELDING PROCEDURE', 'WELDING METHOD']],
      body: [['---', '---', '---']],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 3 },
        1: { cellWidth: (leftColumnWidth * 2) / 3 },
        2: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // JOINT, PWHT, FILLER MET - align with other tables
    autoTable(doc, {
      startY: yPos,
      head: [['JOINT', 'PWHT', 'FILLER MET']],
      body: [['---', '---', '---']],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 3 },
        1: { cellWidth: (leftColumnWidth * 2) / 3 },
        2: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // SURFACE CONDITIONS and COATING
    autoTable(doc, {
      startY: yPos,
      head: [['SURFACE CONDITIONS', 'COATING & THICKNESS']],
      body: [['Painted', 'Paint (0-2mm)']],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: pageWidth * 0.7 - margin },
        1: { cellWidth: pageWidth * 0.3 - margin }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
         // Create certification number with level, cleaning any hidden characters
     const cleanCertNumber = (inspectorData.certificateNo || '---').replace(/[\r\n\t\s]+/g, '').trim();
     const levelSuffix = inspectorData.etLevel ? `-N${inspectorData.etLevel}` : '-N2';
     const certNumber = `STMI / ${cleanCertNumber}${levelSuffix}-E`;
     
     // Parse equipment string (format: "Equipment / Model / SN 12345")
     const equipmentUsed = inspectorData.etEquipment || 'GE Phasec 3 / 801C4RP';
     
     // TEMPERATURE, EQUIPMENT, COMPANY/OPERATOR
     autoTable(doc, {
       startY: yPos,
       head: [['TEMPERATURE OF OBJECT', 'EQUIPMENT USED / SERIAL NO', 'COMPANY / OPERATOR CERT NO.']],
       body: [['0-35°C', equipmentUsed, certNumber]],
       ...tableOptions,
       columnStyles: {
         0: { cellWidth: leftColumnWidth / 2 },
         1: { cellWidth: leftColumnWidth / 2 },
         2: { 
           cellWidth: rightColumnWidth, 
           halign: 'left' as const,
           valign: 'middle' as const
         }
       },
       didParseCell: function(data: any) {
         // Force no line breaks in the certification number column
         if (data.column.index === 2) {
           data.cell.styles.halign = 'left';
           data.cell.styles.valign = 'middle';
           // Remove any line break characters and other problematic characters
           if (typeof data.cell.text === 'string') {
             data.cell.text = data.cell.text
               .replace(/\n/g, ' ')
               .replace(/\r/g, ' ')
               .replace(/\t/g, ' ')
               .replace(/\u00A0/g, ' ') // Non-breaking space
               .replace(/\u200B/g, '') // Zero-width space
               .replace(/\u200C/g, '') // Zero-width non-joiner
               .replace(/\u200D/g, '') // Zero-width joiner
               .replace(/\s+/g, ' ') // Multiple spaces to single space
               .trim();
           }
         }
       }
     });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // REFERENCE BLOCK
    autoTable(doc, {
      startY: yPos,
      head: [['REFERENCE BLOCK USED / SERIAL NUMBER']],
      body: [['Carbon steel 0,5 mm - 1,0 mm - 2,0 mm / CZ1041/14']],
      ...tableOptions
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // PROBE SETTINGS - exactly match column widths and centering
    autoTable(doc, {
      startY: yPos,
      head: [['PROBE TYPE', 'FREQUENCY', 'GAIN', 'PHASE', 'ENFORCEMENT']],
      body: reportData.probeSettings.map(setting => [
        setting.type,
        `${setting.frequency} kHz`,
        `${setting.gain} dB`,
        `${setting.phase}°`,
        setting.enforcement
      ]),
      ...probeTableOptions,
      columnStyles: {
        0: { halign: 'center', cellWidth: (pageWidth - 2*margin) / 5 },
        1: { halign: 'center', cellWidth: (pageWidth - 2*margin) / 5 },
        2: { halign: 'center', cellWidth: (pageWidth - 2*margin) / 5 },
        3: { halign: 'center', cellWidth: (pageWidth - 2*margin) / 5 },
        4: { halign: 'center', cellWidth: (pageWidth - 2*margin) / 5 }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // TEST PROCEDURE
    autoTable(doc, {
      startY: yPos,
      head: [['TEST PROCEDURE']],
      body: [['NDT ET DNVGL-CG-0051 in accordance with ISO 17643']],
      ...tableOptions
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // TEST ACCEPTANCE and EXTENT
    autoTable(doc, {
      startY: yPos,
      head: [['TEST ACCEPTANCE', 'EXTENT OF EXAMINATION']],
      body: [['DNVGL CG 0051, Sec 8 paragraph 9', getExtentOfExamination(reportData)]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: pageWidth * 0.5 - margin },
        1: { cellWidth: pageWidth * 0.5 - margin }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // Calculate how much space is remaining on the page before adding the summary
    const totalUsableHeight = pageHeight - (2 * margin); // Total usable height of the page
    const spaceForResultAndSignatures = 40; // Approximate space needed for result and signatures (in mm)
    const currentY = yPos; // Current Y position after all previous tables
    const remainingSpace = totalUsableHeight - currentY - spaceForResultAndSignatures;

    // Ensure we have a minimum reasonable height
    const summaryHeight = Math.max(remainingSpace, 40);

    // Original table options for summary and beyond - keep original row heights
    const summaryTableOptions = {
      theme: 'plain' as const,
      styles: {
        fontSize: VALUE_SIZE,
        textColor: TEXT_COLOR,
        cellPadding: 1.5,
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
        minCellHeight: 3
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        textColor: LABEL_COLOR,
        fontStyle: 'normal' as const,
        fontSize: LABEL_SIZE,
        cellPadding: 1,
        lineWidth: { top: BORDER_W, right: BORDER_W, bottom: 0, left: BORDER_W },
        lineColor: BORDER_COLOR,
        halign: 'left' as const,
        valign: 'middle' as const
      },
      margin: { left: margin, right: margin },
      tableLineColor: BORDER_COLOR,
      tableLineWidth: BORDER_W
    };

    // SUMMARY - dynamically sized to fill remaining space
    
    let summaryText = `STM has performed ET of following ID(s):\n\n`
    
    // Add Unit ID for POU, POU w/ISO, and custom object types
    if (reportData.objectType !== 'Sling' && reportData.unitId) {
      summaryText += `Unit ID: ${reportData.unitId}\n\n`
    }
    
    // Add Sling ID if present
    if (reportData.slingId) {
      summaryText += `Sling ID: ${reportData.slingId}\n\n`
    }

    // Add comments only if status is "Not Accepted" and comments exist
    if (reportData.status === 'Not Accepted' && reportData.comments && reportData.comments.trim()) {
      summaryText += `${reportData.comments}\n\n`
    }

    autoTable(doc, {
      startY: yPos,
      head: [['SUMMARY']],
      body: [[summaryText]],
      ...summaryTableOptions,
      bodyStyles: {
        fontSize: VALUE_SIZE,
        textColor: TEXT_COLOR,
        minCellHeight: summaryHeight
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // RESULT — shared ET-shaped chrome used across all NDT methods
    const isRejected = reportData.status === 'Not Accepted'
    autoTable(doc, {
      startY: yPos,
      head: [['RESULT']],
      body: [[isRejected ? 'REJECTED' : 'ACCEPTED']],
      ...getNdtResultTableOptions(margin),
      bodyStyles: getNdtResultBodyStyles(isRejected),
    });
    
    // Connect signature boxes directly to the result table - no gap
    // Layout matches MT/LT: full-width ISSUED BY unless a second signer exists.
    yPos = (doc as any).lastAutoTable.finalY;
    const signatureWidth = (pageWidth - (2 * margin)) / 2;
    const hasVerifier = !!verifierName;
    
    // Draw signature boxes with borders (match control card hairline gray)
    doc.setDrawColor(...BORDER_COLOR);
    doc.setLineWidth(BORDER_W);
    
    if (hasVerifier) {
      doc.rect(margin, yPos, signatureWidth, 25);
      doc.rect(margin + signatureWidth, yPos, signatureWidth, 25);
    } else {
      doc.rect(margin, yPos, pageWidth - (2 * margin), 25);
    }

    doc.setFontSize(LABEL_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...LABEL_COLOR);
    if (hasVerifier) {
      doc.text('ISSUED BY', margin + (signatureWidth / 2), yPos + 4, { align: 'center' });
      doc.text('VERIFIED BY', margin + signatureWidth + (signatureWidth / 2), yPos + 4, { align: 'center' });
    } else {
      doc.text('ISSUED BY', margin + ((pageWidth - (2 * margin)) / 2), yPos + 4, { align: 'center' });
    }

    // Signature images
    if (inspectorSignature && typeof inspectorSignature === 'string') {
      if (hasVerifier) {
        doc.addImage(inspectorSignature, 'PNG', margin + (signatureWidth / 2) - 20, yPos + 8, 40, 10);
      } else {
        doc.addImage(inspectorSignature, 'PNG', (pageWidth / 2) - 20, yPos + 8, 40, 10);
      }
    }
    if (hasVerifier && verifierSignature && typeof verifierSignature === 'string') {
      doc.addImage(verifierSignature, 'PNG', margin + signatureWidth + (signatureWidth / 2) - 20, yPos + 8, 40, 10);
    }

    // Name / date in the value style so signatures match the prototype's
    // value rows (VALUE_SIZE, TEXT_COLOR, normal weight).
    doc.setFontSize(VALUE_SIZE);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_COLOR);
    const signatureSite = reportData.testSite || 'Bergen'
    if (hasVerifier) {
      doc.text(`${getFullName(reportData.issuedBy)} / ${formatDate(reportData.testDate)} / ${signatureSite}`,
               margin + (signatureWidth / 2), yPos + 22, { align: 'center' });
      doc.text(
        `${getFullName(verifierName)} / ${formatDate(verifierDate)} / ${signatureSite}`,
        margin + signatureWidth + (signatureWidth / 2), yPos + 22, { align: 'center' },
      );
    } else {
      doc.text(`${getFullName(reportData.issuedBy)} / ${formatDate(reportData.testDate)} / ${signatureSite}`,
               pageWidth / 2, yPos + 22, { align: 'center' });
    }
    
    // Add image pages if images are attached
    if (reportData.attachedImages && reportData.attachedImages.length > 0) {
      reportData.attachedImages.forEach((imageData, index) => {
        doc.addPage();
        
        // Add header with title and logo on image page (same gray as main title)
        drawMethodTitle(doc, 'Eddy Current', margin, margin, { suffix: ` - IMAGE ${index + 1}` });
        
        // Add logo on image page
        if (logoDataUrl) {
          doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12);
        }
        
        // Add slim border starting right after header (match control card hairline gray)
        doc.setDrawColor(...BORDER_COLOR);
        doc.setLineWidth(BORDER_W);
        const borderY = margin + 10;
        const borderHeight = pageHeight - borderY - margin - 10;
        doc.rect(margin, borderY, pageWidth - 2 * margin, borderHeight);
        
        // Calculate available space inside border
        const availableWidth = pageWidth - 2 * margin - 2; // Account for border width
        const availableHeight = borderHeight - 2; // Account for border width
        
        let currentY = borderY + 2; // Start just inside the border
        
        // Add image comment inside border if it exists
        if (imageData.comment && imageData.comment.trim()) {
          doc.setFontSize(VALUE_SIZE);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...TEXT_COLOR);
          doc.text(imageData.comment, margin + 2, currentY + 5);
          currentY += 10; // Move down for comment
        }
        
        // Calculate remaining space for image
        const remainingHeight = availableHeight - (currentY - borderY - 2);
        
        // Add image within the border
        try {
          // Use a much smaller, conservative size to prevent blow-up
          const maxWidth = Math.min(availableWidth * 0.6, 120); // Max 120mm or 60% of available width
          const maxHeight = Math.min(remainingHeight * 0.6, 80); // Max 80mm or 60% of available height
          
          // Center the image
          const imgX = margin + 2 + (availableWidth - maxWidth) / 2;
          const imgY = currentY + (remainingHeight - maxHeight) / 2;
          
          // Add the image with conservative dimensions
          doc.addImage(imageData.image, 'JPEG', imgX, imgY, maxWidth, maxHeight, '', 'FAST');
        } catch (error) {
          // Add error text if image fails to load
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          doc.text('Error loading attached image', pageWidth / 2, pageHeight / 2, { align: 'center' });
        }
      });
    }
    
    // Footer matching the original exactly
    const pageCount = (doc.internal as any).getNumberOfPages?.() || doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(VALUE_SIZE);
      doc.setTextColor(...TEXT_COLOR);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.setFontSize(LABEL_SIZE);
      doc.setTextColor(...LABEL_COLOR);
      doc.text('STM Inspection AS. NO 997 553 608 MVA. Phone: +47 55 94 30 90. Web: stmi.no email: inspection@stmas.no',
                pageWidth / 2, pageHeight - 5, { align: 'center' });
    }
    
    // Convert to blob and return
    const pdfBlob = doc.output('blob');
    return pdfBlob;
  } catch (error) {
    throw error;
  }
}

export const generateETReport = generatePDF; 